import { Link as RouterLink } from 'react-router-dom';
import { Box, Divider, Flex } from '@chakra-ui/react';
import { Checkbox, Text } from '@kula-ai/components';
import { jobStatusConfig } from '@/constants/job-constants';
import { JOB_PIPELINE_UNPUBLISHED } from '../../../constants';
import { Card, StatusPill } from '../primitives';
import { toInternalPath } from '../../../helpers';
import { useBlockFollowup } from '../../../hooks';
import { useAgentForgeStore } from '../../../stores';
import type { JobCardData, JobListBlockData } from '../../../types';
import { SelectionBar } from '../SelectionBar';
import { SuggestedActionChips } from '../SuggestedActionChips';
import {
  gateJobActions,
  resolveJobSelection,
} from '../../../helpers/jobListActions';

interface JobListBlockProps {
  data: JobListBlockData;
}

// Reuse the ATS job-status config (the same source the jobs page uses) so the
// dot colour + label match everywhere — published/live/open read as "Open" with
// the status-green (#73D017) dot. Aliases fold the agent's status values onto
// the canonical config keys.
type StatusKey = keyof typeof jobStatusConfig;
const STATUS_ALIAS: Record<string, StatusKey> = {
  live: 'published',
  open: 'published',
  published: 'published',
  draft: 'draft',
  pending_approval: 'pending_approval',
  scheduled: 'scheduled',
  closed: 'closed',
  rejected: 'rejected',
  archived: 'archived',
};

const StatusBadge = ({ status }: { status?: string }) => {
  if (!status) return null;
  const cfg = jobStatusConfig[STATUS_ALIAS[status.toLowerCase()]];
  return <StatusPill label={cfg?.label ?? status} color={cfg?.color} />;
};

// A live/published job is the only state with a candidate pipeline; everything
// else (draft, pending, scheduled) has nothing to show yet.
const isPublished = (status?: string): boolean =>
  !!status && STATUS_ALIAS[status.toLowerCase()] === 'published';

// The meta line: department · office · workplace · employment type. Only the
// parts we actually have, joined with dots.
const metaLine = (job: JobCardData): string =>
  [job.department, job.office, job.workplace, job.employmentType]
    .filter(Boolean)
    .join(' · ');

// Second detail line — the disambiguators for same-title jobs: requisition
// code and hiring team (the date now sits top-right).
const teamLine = (team?: string[]): string | undefined => {
  if (!team?.length) return undefined;
  const shown = team.slice(0, 3).join(', ');
  const more = team.length - 3;
  return `Team: ${shown}${more > 0 ? ` +${more}` : ''}`;
};

const detailLine = (job: JobCardData): string =>
  [job.code && `Req ${job.code}`, teamLine(job.hiringTeam)]
    .filter(Boolean)
    .join(' · ');

const JobCard = ({
  job,
  checked,
  onToggle,
}: {
  job: JobCardData;
  /** undefined = list is not selectable (single-job confirmation card). */
  checked?: boolean;
  onToggle?: () => void;
}) => {
  const openFloatingBridge = useAgentForgeStore((s) => s.openFloatingBridge);
  const meta = metaLine(job);
  const detail = detailLine(job);
  const time = job.openedAt ?? job.createdAt;
  const pipelineNote = isPublished(job.status)
    ? undefined
    : JOB_PIPELINE_UNPUBLISHED;
  const selectable = typeof checked === 'boolean' && !!onToggle;
  return (
    <Card selected={!!checked}>
      <Flex align="flex-start" justify="space-between" gap="10px">
        <Flex align="flex-start" gap="10px" minW="0" flex="1">
          {selectable && (
            <Box mt="2px">
              <Checkbox
                size="sm"
                checked={checked}
                onChange={onToggle}
                aria-label={`Select ${job.title}`}
              />
            </Box>
          )}
          {/* Title + status sit together (badge trails the title); the title
              deep-links to the job (internal ATS route) in the same tab,
              bridging the chat overlay — same as candidate rows. Clamps to two
              lines; overflow:hidden + minW:0 up the flex chain keeps the card
              from stretching to the full title width. */}
          <Flex align="center" gap="8px" minW="0" flexWrap="wrap">
            {job.url ? (
              <Box
                as={RouterLink}
                to={toInternalPath(job.url)}
                minW="0"
                maxW="100%"
                overflow="hidden"
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  openFloatingBridge(toInternalPath(job.url as string));
                }}
                _hover={{ textDecoration: 'underline' }}
                data-test-id={`agentforge-job-link-${job.id}`}
              >
                <Text className="text-secondary-900 font-semibold line-clamp-2">
                  {job.title}
                </Text>
              </Box>
            ) : (
              <Text className="text-secondary-900 font-semibold line-clamp-2">
                {job.title}
              </Text>
            )}
            {job.confidential && <StatusPill label="Confidential" />}
            <StatusBadge status={job.status} />
          </Flex>
        </Flex>
        {time && (
          <Text
            variant="small"
            className="text-secondary-400"
            style={{ flexShrink: 0, whiteSpace: 'nowrap' }}
          >
            {time}
          </Text>
        )}
      </Flex>
      {meta && (
        <Text
          variant="small"
          className="text-secondary-500 line-clamp-2"
          style={{ marginTop: 4, display: 'block' }}
        >
          {meta}
        </Text>
      )}
      {detail && (
        <Text
          variant="small"
          truncate
          className="text-secondary-400"
          style={{ marginTop: 2, display: 'block' }}
        >
          {detail}
        </Text>
      )}
      {pipelineNote && (
        <>
          <Divider borderColor="border-color" my="10px" />
          <Text variant="small" className="text-secondary-400">
            {pipelineNote}
          </Text>
        </>
      )}
    </Card>
  );
};

// A scannable list of job cards. Used for search results, job pickers, and the
// created-job confirmation — one consistent card everywhere a job appears.
// Multi-job lists are SELECTABLE like candidate cards: tick jobs, then the
// chips below act on the selection (falling back to the whole list when
// nothing is ticked). Follow-up chips render below the cards, primary first.
export const JobListBlock = ({ data }: JobListBlockProps) => {
  const blockKey = data.toolCallId ?? 'no-tool-call-id';
  const selection = useAgentForgeStore((s) => s.selectionByBlock[blockKey]);
  const toggle = useAgentForgeStore((s) => s.toggleBlockSelection);
  const setSelection = useAgentForgeStore((s) => s.setBlockSelection);
  const clearSelection = useAgentForgeStore((s) => s.clearBlockSelection);
  const jobs = data.jobs ?? [];
  const allIds = data.ids ?? jobs.map((j) => j.id);
  const ownSelectedIds = resolveJobSelection(selection, jobs);
  const { sendFollowup, withCounts, isSubmitting } = useBlockFollowup({
    toolCallId: data.toolCallId,
    allIds,
    selectedIds: ownSelectedIds,
    nameById: Object.fromEntries(jobs.map((j) => [j.id, j.title])),
    // Render the job title in the sent bubble; pass the id to the agent via a
    // hidden marker so "publish job 21480" reads as the job name.
    idAsName: { ctxKey: 'job_id', fallbackLabel: 'the job' },
  });
  if (jobs.length === 0) return null;

  // Selection UX only makes sense with a real choice.
  const selectable = jobs.length > 1;

  // Gate chips against the job the action will actually target — {first_id}
  // fills from selectedIds[0] when anything is checked, else the top row.
  // CORE-4840: a published job must not offer "Publish job post".
  const targetJob =
    (ownSelectedIds.length > 0 &&
      jobs.find((j) => j.id === ownSelectedIds[0])) ||
    jobs[0];
  const actions = withCounts(gateJobActions(data.suggestedActions, targetJob));

  return (
    <Flex direction="column" gap="8px">
      {data.summary && (
        <Text variant="small" className="text-secondary-500">
          {data.summary}
        </Text>
      )}
      {selectable && (
        <SelectionBar
          selected={ownSelectedIds.length}
          total={allIds.length}
          noun="jobs"
          onSelectAll={() => setSelection(blockKey, allIds)}
          onClear={() => clearSelection(blockKey)}
          testId="agentforge-joblist-selection"
        />
      )}
      {jobs.map((job) => (
        <JobCard
          key={job.id}
          job={job}
          checked={selectable ? ownSelectedIds.includes(job.id) : undefined}
          onToggle={selectable ? () => toggle(blockKey, job.id) : undefined}
        />
      ))}
      {actions && actions.length > 0 && (
        <Box mt="4px">
          <SuggestedActionChips
            actions={actions}
            onSelect={sendFollowup}
            disabled={isSubmitting}
            testIdPrefix="joblist-suggested-action"
          />
        </Box>
      )}
    </Flex>
  );
};
